import React, { useState, useEffect } from "react";
import { ConfirmDeleteModal } from "../../../../../../Components/ConfirmDeleteModal";
import {
  TableColumnsType,
  Button,
  Space,
  Dropdown,
  Menu,
  Modal,
  Form,
  Input,
  Select,
  Divider,
  message,
  Spin,
  Pagination,
  Typography,
} from "antd";
import { Icon } from "@iconify/react";
import * as Yup from "yup";
import { Formik } from "formik";
import { debounce } from "lodash";

import { DataTable } from "../../../../../../Components/DataTable/DataTable";
import { panelApiUrl } from "~/Utils";
import { api, remove } from "../../../../../../../SPA/Services/api.service";
import { getFormattedDate, getKey } from "@utils/CommonUtils";
import { updatedAtOf, updatedByOf } from "../../../../../../Utils/auditFields";
import { apiSortField } from "../../../../../../Utils/sortField";
import styles from "@styles/variables.module.scss";



export const SubRegionList = () => {
  const [subRegionData, setSubRegionData] = useState<any[]>([]);
  /* The list URL, re-requested page by page by DataTable's Export (as VendorList). */
  const [subRegionExportUrl, setSubRegionExportUrl] = useState<string>("");
  const [subRegionLoading, setSubRegionLoading] = useState(false);
  const [subRegionError, setSubRegionError] = useState<string | null>(null);
  const [editingSubRegion, setEditingSubRegion] = useState<any>(null);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "view">("add");
  const [deletingSubRegionId, setDeletingSubRegionId] = useState<string | null>(null);
  const [deleteSubRegionModal, setDeleteSubRegionModal] = useState(false);
  const [deleteSubRegionLoading, setDeleteSubRegionLoading] = useState(false);
  const [openSubRegionModal, setOpenSubRegionModal] = useState(false);
  const [selectedRegionFilter, setSelectedRegionFilter] = useState<string | undefined>(undefined);
  const [subRegionTotal, setSubRegionTotal] = useState<number>(0);
  const [subRegionPage, setSubRegionPage] = useState<number>(0);
  const [subRegionSearch, setSubRegionSearch] = useState<string>("");
  const [clearSubRegionSearch, setClearSubRegionSearch] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [regionData, setRegionData] = useState<any[]>([]);
  const [filterRegionData, setFilterRegionData] = useState<any[]>([]);
  const [sortOrder, setSortOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdOn");
  const [existingSubRegions, setExistingSubRegions] = useState<any[]>([]);

  useEffect(() => {
    if (openSubRegionModal) {
      api
        .get(`${panelApiUrl}/subRegion/get-subregion-list?page=0&size=1000`)
        .then((res: any) => {
          const records = res?.data?.response?.data?.records || [];
          setExistingSubRegions(records);
        })
        .catch(() => setExistingSubRegions([]));
    }
  }, [openSubRegionModal]);

  const subRegionSchema = Yup.object().shape({
    regionId: Yup.string().required("Please select a region"),
    subRegionName: Yup.string()
      .required("Please enter a sub region")
      .min(2, "Sub region must be at least 2 characters long")
      .max(50, "Sub region must not exceed 50 characters")
      .trim("Leading and trailing spaces are not allowed")
      .strict(true)
      /* Unique WITHIN a region, not across all of them: "Kuantan KUA" under
         East Coast and again under Northern are different places. `this.parent`
         is the form's values, so the region being chosen is in scope. */
      .test("unique", "This name already exists under the selected region.", function (value) {
        if (!value) return true;
        const regionId = String((this.parent as any)?.regionId ?? "");
        if (!regionId) return true; // nothing to scope by yet
        const isDuplicate = existingSubRegions.some(
          (sr: any) =>
            sr.subRegionName?.toLowerCase() === value.toLowerCase() &&
            String(sr.regionId ?? sr.region?.id ?? "") === regionId &&
            sr.id !== editingSubRegion?.id
        );
        return !isDuplicate;
      }),
  });

  const recordsPerPage = 10;

  useEffect(() => {
    fetchRegionData();
  }, []);

  useEffect(() => {
    setSubRegionPage(0);
    fetchSubRegionData(subRegionSearch, 0, sortOrder, orderBy);
  }, [selectedRegionFilter]);

  const fetchRegionData = () => {
    api
      .get(`${panelApiUrl}/region/get-region-list?page=0&size=1000`)
      .then((res: any) => {
        const responseData = res?.data?.response?.data?.records || res?.data?.data || [];
        const sortedData = [...responseData].sort((a: any, b: any) => (a.regionName || "").localeCompare(b.regionName || ""));
        setRegionData(sortedData);
      })
      .catch((err) => {
        console.error("Error fetching regions:", err);
      });
  };

  const handleSubRegionSearchChange = debounce((value: string, isLoading: boolean) => {
    setClearSubRegionSearch(false);
    setSubRegionSearch(value);
    setSubRegionPage(0);
    setSubRegionLoading(isLoading);
    fetchSubRegionData(value, 0, sortOrder, orderBy);
  }, 1000);

  const handleTableSort = (column: any, currentSortOrder: string) => {
    let order = "";
    if (column.sorter) {
      if (currentSortOrder === "asc") {
        setSortOrder("desc");
        order = "desc";
      } else if (currentSortOrder === "desc") {
        setSortOrder("asc");
        order = "asc";
      } else {
        setSortOrder("desc");
        order = "desc";
      }
    }
    setOrderBy(column.dataIndex);
    setSubRegionPage(0);
    fetchSubRegionData(subRegionSearch, 0, order, column.dataIndex);
  };

  const fetchSubRegionData = (searchText: string = "", currentPage: number = 0, currentOrder: string = sortOrder, currentOrderBy: string = orderBy) => {
    setSubRegionLoading(true);
    setSubRegionError(null);

    let url = `${panelApiUrl}/subRegion/get-subregion-list?page=${currentPage}&size=${recordsPerPage}&searchText=${encodeURIComponent(
      searchText
    )}&order=${currentOrder}&orderBy=${apiSortField(currentOrderBy)}`;
    setSubRegionExportUrl(url);
    if (selectedRegionFilter) {
      url += `&regionId=${selectedRegionFilter}`;
    }

    api
      .get(url)
      .then((res: any) => {
        const responseData = res?.data?.response?.data;
        const records = responseData?.records || [];
        setSubRegionTotal(responseData?.totalCount || 0);

        const mappedData = records.map((item: any, index: any) => ({
          key: getKey(currentPage, index),
          id: item.id,
          regionId: item.regionId,
          regionName: item.region?.regionName || item.regionName || "-",
          subRegionName: item.subRegionName || "-",
          createdAt: getFormattedDate(item.createdOn) || "-",
          createdBy: item.createdBy || "-",
          modifiedAt: updatedAtOf(item),
          modifiedBy: updatedByOf(item),
          ...item,
        }));
        setSubRegionData(mappedData);

        const uniqueRegionsMap = new Map();
        records.forEach((record: any) => {
          const rId = record.regionId || record.region?.id;
          const rName = record.regionName || record.region?.regionName;
          if (rId && rName) {
            uniqueRegionsMap.set(rId, { id: rId, regionName: rName });
          }
        });
        const uniqueRegions = Array.from(uniqueRegionsMap.values());
        uniqueRegions.sort((a: any, b: any) => (a.regionName || "").localeCompare(b.regionName || ""));
        setFilterRegionData(uniqueRegions);
      })
      .catch((err: any) => {
        console.error("Error fetching sub region data:", err);
        setSubRegionData([]);
        setSubRegionTotal(0);
        setSubRegionError("Failed to fetch sub region data");
        message.error("Error fetching sub region data");
      })
      .finally(() => {
        setSubRegionLoading(false);
      });
  };

  const handleSubRegionMenuClick = (key: string, record: { [key in string]: any }) => {
    if (key === "edit") {
      setEditingSubRegion(record);
      setModalMode("edit");
      setOpenSubRegionModal(true);
    } else if (key === "delete") {
      setDeletingSubRegionId(record?.id);
      setDeleteSubRegionModal(true);
    }
  };

  const handleDeleteSubRegion = () => {
    setDeleteSubRegionLoading(true);
    remove(`${panelApiUrl}/subRegion/${deletingSubRegionId}`)
      .then((res: any) => {
        setDeleteSubRegionLoading(false);
        message.success(res?.data?.message || "Sub region deleted successfully");
        setDeleteSubRegionModal(false);
        setDeletingSubRegionId(null);
        fetchSubRegionData(subRegionSearch, subRegionPage, sortOrder, orderBy);
      })
      .catch((err: any) => {
        setDeleteSubRegionLoading(false);
        message.error(err?.data?.message || "Failed to delete sub region");
        setDeleteSubRegionModal(false);
        setDeletingSubRegionId(null);
      });
  };

  const handleCloseSubRegionModal = () => {
    setOpenSubRegionModal(false);
    setEditingSubRegion(null);
  };

  const subRegionColumns: TableColumnsType = [
    {
      title: "Region",
      dataIndex: "regionName",
      sorter: true,
      sortOrder: sortOrder === "asc" && orderBy === "regionName" ? "ascend" : sortOrder === "desc" && orderBy === "regionName" ? "descend" : undefined,
    },
    {
      title: "Sub Region",
      dataIndex: "subRegionName",
      sorter: true,
      sortOrder: sortOrder === "asc" && orderBy === "subRegionName" ? "ascend" : sortOrder === "desc" && orderBy === "subRegionName" ? "descend" : undefined,
    },
    {
      title: "Created At",
      dataIndex: "createdOn",
      sorter: true,
      sortOrder: sortOrder === "asc" && orderBy === "createdOn" ? "ascend" : sortOrder === "desc" && orderBy === "createdOn" ? "descend" : undefined,
      render: (value: any) => getFormattedDate(value) || "-",
    },
    {
      title: "Created By",
      dataIndex: "createdBy",
      sorter: true,
      sortOrder: sortOrder === "asc" && orderBy === "createdBy" ? "ascend" : sortOrder === "desc" && orderBy === "createdBy" ? "descend" : undefined,
    },
    {
      title: "Updated At",
      dataIndex: "updatedOn",
      sorter: true,
      sortOrder: sortOrder === "asc" && orderBy === "updatedOn" ? "ascend" : sortOrder === "desc" && orderBy === "updatedOn" ? "descend" : undefined,
      render: (value: any) => getFormattedDate(value) || "-",
    },
    {
      title: "Updated By",
      dataIndex: "updatedBy",
      sorter: true,
      sortOrder: sortOrder === "asc" && orderBy === "updatedBy" ? "ascend" : sortOrder === "desc" && orderBy === "updatedBy" ? "descend" : undefined,
    },
    {
      title: "Action",
      dataIndex: " ",
      width: 90,
      render: (value: any, record: any) => {
        const menuItems = [
          {
            label: (
              <Typography.Text
                type="secondary"
                style={{ color: styles?.primary }}
              >
                <Space>
                  <Icon icon="akar-icons:edit" inline fontSize={16} style={{ color: styles?.primary }} />
                  Edit
                </Space>
              </Typography.Text>
            ),
            key: "edit",
          },
          {
            label: (
              <Typography.Text
                type="danger"
              >
                <Space>
                  <Icon icon="fluent:delete-48-regular" inline fontSize={16} />
                  Delete
                </Space>
              </Typography.Text>
            ),
            key: "delete",
          },
        ];
        return (
          <Dropdown
            overlay={
              <Menu
                /* A portalled menu bubbles up the React tree, so the row
                   click handler on the cell would fire too. */
                onClick={({ key, domEvent }) => {
                  domEvent.stopPropagation();
                  handleSubRegionMenuClick(key, record);
                }}
                items={menuItems}
              />
            }
            trigger={["click"]}
            placement="bottomLeft"
          >
            <span onClick={(e) => e.stopPropagation()}>
              <Icon icon={"bi:three-dots-vertical"} style={{ cursor: "pointer" }} />
            </span>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <>
      {subRegionLoading && subRegionData.length === 0 ? (
        <div style={{ textAlign: "center", padding: "50px 0" }}>
          <Spin size="large" />
        </div>
      ) : subRegionError ? (
        <Typography.Text type="danger">{subRegionError}</Typography.Text>
      ) : (
        <>
          <DataTable
            multiSelect={true}
            hideExport={false}
            /* The three-dots Action column is a menu, not data. */
            excludeExportCols={["Action"]}
            exportFileName="Sub Region"
            exportApiUrl={subRegionExportUrl}
            columns={subRegionColumns?.map((column) => ({
              ...column,
              onHeaderCell: (column: any) => ({
                onClick: () => {
                  if (column.sorter) {
                    handleTableSort(column, sortOrder);
                  }
                },
              }),
            }))}
            tableData={subRegionData}
            className="table-clickable"
            onClick={(record: any) => {
              setEditingSubRegion(record);
              setModalMode("view");
              setOpenSubRegionModal(true);
            }}
            searchPlaceholderText="Search by sub region name"
            searchQuery={subRegionSearch}
            clearStringSearch={clearSubRegionSearch}
            onSearchChange={handleSubRegionSearchChange}
            inputDisable={subRegionLoading}
            isPagination={true}
            isServerSearchApi={true}
            totalRecords={subRegionTotal}
            recordsPerPage={recordsPerPage}
            additionalCTAToRight={true}
            additionalCTA={
              <Space size={8}>
                <Select
                  placeholder="Region"
                  allowClear
                  style={{ width: 150 }}
                  value={selectedRegionFilter}
                  onChange={(value) => setSelectedRegionFilter(value)}
                >
                  {regionData.map((region) => (
                    <Select.Option key={region.id} value={region.id}>
                      {region.regionName}
                    </Select.Option>
                  ))}
                </Select>
                <Button
                  type="primary"
                  onClick={() => {
                    setEditingSubRegion(null);
                    setModalMode("add");
                    setOpenSubRegionModal(true);
                  }}
                >
                  <Space>
                    <Icon icon="akar-icons:plus" inline />
                    Add
                  </Space>
                </Button>
              </Space>
            }
          />
          <div className="pagination">
            <Pagination
              defaultCurrent={1}
              current={subRegionPage + 1}
              total={subRegionTotal}
              onChange={(newPage) => {
                const startIndex = newPage - 1;
                setSubRegionPage(startIndex);
                fetchSubRegionData(subRegionSearch, startIndex, sortOrder, orderBy);
              }}
              showSizeChanger={false}
              pageSize={10}
            />
          </div>
        </>
      )}

      {/* Add / Edit / View Sub Region Modal */}
      <Modal
        title={modalMode === "view" ? "View Sub Region" : editingSubRegion ? "Edit Sub region" : "Add Sub region"}
        open={openSubRegionModal}
        maskClosable={false}
        centered
        width="30%"
        onCancel={handleCloseSubRegionModal}
        footer={null}
        destroyOnClose={true}
      >
        <Divider style={{ margin: "-20px 0 22px", borderTop: "1px solid #d9d9d9" }} />

        <Formik
          key={editingSubRegion?.id || "add"}
          enableReinitialize={true}
          initialValues={{
            regionId: editingSubRegion?.regionId || undefined,
            subRegionName: editingSubRegion?.subRegionName || "",
          }}
          validationSchema={subRegionSchema}
          validateOnMount
          onSubmit={(values, { resetForm }) => {
            setIsLoading(true);

            const payload = {
              regionId: values.regionId,
              subRegionName: values.subRegionName,
            };

            if (editingSubRegion) {
              api
                .put(`${panelApiUrl}/subRegion/${editingSubRegion.id}`, payload)
                .then((res: any) => {
                  message.success(res?.data?.message || "Sub region updated successfully");
                  setIsLoading(false);
                  resetForm();
                  handleCloseSubRegionModal();
                  fetchSubRegionData(subRegionSearch, subRegionPage, sortOrder, orderBy);
                })
                .catch((err: any) => {
                  message.error(err?.data?.message || "Failed to update sub region");
                  setIsLoading(false);
                });
            } else {
              api
                .post(`${panelApiUrl}/subRegion`, payload)
                .then((res: any) => {
                  message.success(res?.data?.message || "Sub region added successfully");
                  setIsLoading(false);
                  resetForm();
                  handleCloseSubRegionModal();
                  fetchSubRegionData(subRegionSearch, subRegionPage, sortOrder, orderBy);
                })
                .catch((err: any) => {
                  message.error(err?.data?.message || "Failed to add sub region");
                  setIsLoading(false);
                });
            }
          }}
        >
          {({ values, errors, touched, setFieldValue, handleChange, handleBlur, handleSubmit, isValid, dirty }) => (
            <Form layout="vertical" onFinish={handleSubmit}>
              <Form.Item required
                label="Region"
                validateStatus={touched.regionId && errors.regionId ? "error" : ""}
                help={touched.regionId && typeof errors.regionId === "string" ? errors.regionId : undefined}
              >
                <Select
                  placeholder="Select Region"
                  value={values.regionId}
                  onChange={(value) => setFieldValue("regionId", value)}
                  onBlur={handleBlur}
                  style={{ width: "100%" }}
                  disabled={modalMode === "view"}
                >
                  {regionData.map((region) => (
                    <Select.Option key={region.id} value={region.id}>
                      {region.regionName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item required
                label="Sub Region"
                validateStatus={touched.subRegionName && errors.subRegionName ? "error" : ""}
                help={touched.subRegionName && typeof errors.subRegionName === "string" ? errors.subRegionName : undefined}
              >
                <Input
                  name="subRegionName"
                  value={values.subRegionName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter sub region name"
                  maxLength={50}
                  disabled={modalMode === "view"}
                />
              </Form.Item>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 14, marginTop: 32 }}>
                <Button onClick={handleCloseSubRegionModal} disabled={isLoading}>
                  {modalMode === "view" ? "Close" : "Cancel"}
                </Button>

                {modalMode !== "view" && (
                  <Button type="primary" htmlType="submit" disabled={!dirty || !isValid} loading={isLoading}>
                    {editingSubRegion ? "Update" : "Add"}
                  </Button>
                )}
              </div>
            </Form>
          )}
        </Formik>
      </Modal>

      {/* Delete Sub Region Modal */}
      <ConfirmDeleteModal
        open={deleteSubRegionModal}
        message="Are you sure you want to delete the sub region?"
        isConfirming={deleteSubRegionLoading}
        onCancel={() => {
          setDeleteSubRegionModal(false);
          setDeletingSubRegionId(null);
        }}
        onConfirm={handleDeleteSubRegion}
      />
    </>
  );
};
